import {
  AttributeTranslation,
  DayThemeTranslation,
  HighlightTranslation,
  LevelTranslation,
  SegmentTranslation,
  VenueTypeTranslation,
} from '../types/translation';
import { hasOwnProperty } from './utils';
import {
  BrandAttribute,
  BrandCommonTextValues,
  BrandDayTheme,
  BrandEventHighlight,
  BrandLevel,
  BrandSegment,
  BrandVenueType,
  CommonTexts,
  CommonTextsLanguageOptions,
} from '../types/brand';
import { mapBy } from './functional';
import { translateCommonTexts } from './commonTexts';
import {
  DEFAULT_24_7_WORD,
  DEFAULT_AFT_WORD,
  DEFAULT_CLOSED_WORD,
  DEFAULT_DECK_WORD,
  DEFAULT_ENDS_LATE_WORD,
  DEFAULT_FWD_WORD,
  DEFAULT_MID_WORD,
  DEFAULT_PRT_WORD,
  DEFAULT_START_TIME_WORD,
  DEFAULT_STB_WORD,
} from './venue';

export function translateBrandSegment(
  segment: BrandSegment,
  translation?: SegmentTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...segment,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}

export function translateBrandLevel(
  level: BrandLevel,
  translation?: LevelTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...level,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}

export function translateBrandAttribute(
  attribute: BrandAttribute,
  translation?: AttributeTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...attribute,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}

export function translateBrandVenueType(
  venueType: BrandVenueType,
  translation?: VenueTypeTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...venueType,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}

export function translateBrandDayTheme(
  dayTheme: BrandDayTheme,
  translation?: DayThemeTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...dayTheme,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}

export function translateBrandHighlight(
  highlight: BrandEventHighlight,
  translation?: HighlightTranslation,
  lang?: string
) {
  if (!translation || !lang) return undefined;
  const { transforms: transforms } = translation;

  if (!transforms || !hasOwnProperty(transforms, lang) || !transforms[lang].active) {
    return undefined;
  }

  return {
    ...highlight,
    name: transforms[lang].delta.name,
    _lang: lang,
  };
}

export const getCommonTexts = (
  commonTexts: CommonTexts,
  language: CommonTextsLanguageOptions
): BrandCommonTextValues => {
  let texts = commonTexts.default;

  if (language.selected && commonTexts.translations.length) {
    let commonTextTranslationsMap = mapBy(commonTexts.translations, 'refers');

    texts = commonTexts.default.map(
      ct => translateCommonTexts(ct, commonTextTranslationsMap[ct._id], language.selected) || ct
    );
  }

  let [closed] = texts.filter(txt => txt.label === 'venue_closed');
  let [late] = texts.filter(txt => txt.label === 'ends_late');
  let [deck] = texts.filter(txt => txt.label === 'deck');
  let [venue_location_fwd] = texts.filter(txt => txt.label === 'venue_location_fwd');
  let [venue_location_mid] = texts.filter(txt => txt.label === 'venue_location_mid');
  let [venue_location_aft] = texts.filter(txt => txt.label === 'venue_location_aft');
  let [venue_location_prt] = texts.filter(txt => txt.label === 'venue_location_prt');
  let [venue_location_stb] = texts.filter(txt => txt.label === 'venue_location_stb');
  let [twenty_four_seven] = texts.filter(txt => txt.label === '24_7');
  let [over_ride_start_time] = texts.filter(txt => txt.label === 'override_start_time');

  return {
    venue_closed: closed?.name ?? DEFAULT_CLOSED_WORD,
    ends_late: late?.name ?? DEFAULT_ENDS_LATE_WORD,
    deck: deck?.name ?? DEFAULT_DECK_WORD,
    venue_location_fwd: venue_location_fwd?.name ?? DEFAULT_FWD_WORD,
    venue_location_mid: venue_location_mid?.name ?? DEFAULT_MID_WORD,
    venue_location_aft: venue_location_aft?.name ?? DEFAULT_AFT_WORD,
    venue_location_prt: venue_location_prt?.name ?? DEFAULT_PRT_WORD,
    venue_location_stb: venue_location_stb?.name ?? DEFAULT_STB_WORD,
    twenty_four_seven: twenty_four_seven?.name ?? DEFAULT_24_7_WORD,
    override_start_time: over_ride_start_time?.name ?? DEFAULT_START_TIME_WORD,
  };
};
